<script>
import i18n from "@/plugins/i18n";

export default {
    props: {
        title: {
            default: "Pop-Up Title",
            type: String
        },

        header: {
            default: true,
            type: Boolean
        },

        footer: {
            default: true,
            type: Boolean
        },

        okButtonText: {
            default: i18n.t('myCompanies.company_table.ok'),
            type: String
        },

        cancelButtonText: {
            default: i18n.t('myCompanies.company_table.cancel'),
            type: String
        },

        showOkButton: {
            default: true,
            type: Boolean
        },
        
        showCancelButton: {
            default: true,
            type: Boolean
        },

        size: {
            default: "md",
            type: String
            /* 
                xs -> 20%
                sm -> 35%
                md -> 50%
                lg -> 65%
                xl -> 80%
            */
        },

        customStyle: {
            type: Object,
            required: false,
        },

        bodyCustomStyle: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            isVisible: false,
            hideInProgress: false
        }
    },

    computed: {
        bodyWidth() {
            const size = this.size;
            switch (size) {
                case "xs":
                    return "20%";
                case "sm":
                    return "35%";
                case "md":
                    return "50%";
                case "lg":
                    return "65%";
                case "xl":
                    return "80%";
                default:
                    return "50%"
            }
        },
    },

    mounted() {

    },

    methods: {
        openPopup() {
            this.emitOnShow();
            this.isVisible = true;
        },

        closePopup() {
            this.emitOnHide();
            this.isVisible = false;
            this.hideInProgress = false;
        },

        emitOnShow() {
            this.$emit("onShow");
        },

        emitOnHide() {
            this.$emit("onHide");
        },

        emitOk() {
            this.startHideProcess();
            this.$emit("ok");
        },

        startHideProcess() {
            this.hideInProgress = true;
            setTimeout(() => {
                this.closePopup();
            }, 150);
        }
    }
}
</script>

<template>
    <portal>
    <div class="sust-popup" :class="{'hide': hideInProgress}" v-if="isVisible" @mousedown.self="startHideProcess()" @keydown.esc="startHideProcess()">
        <div class="popup-ctr" 
            :class="{'hide': hideInProgress}" 
            :style="{
                width: bodyWidth,
                ...customStyle
            }"
        >
            <header v-if="header">
                <div class="title">
                    {{ title }}
                </div>
                <button class="close-button" @click="startHideProcess()">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </header>

            <div class="body" :style="{...bodyCustomStyle}">
                <slot></slot>
            </div>

            <slot name="footer" :hide="startHideProcess">
                <footer v-if="footer">
                    <button v-if="showCancelButton" 
                        class="cancel" 
                        @click="startHideProcess()"
                    >
                        {{ cancelButtonText }}
                    </button>
                    <button v-if="showOkButton" 
                        class="ok" 
                        @click="emitOk()"
                    >
                        {{ okButtonText }}
                    </button>
                </footer>
            </slot>
        </div>
    </div>
</portal>
</template>

<style lang='scss' scoped>
    .sust-popup {
        animation: BackdropFadeIn 150ms ease-in;
        animation-fill-mode: forwards;

        margin: 0 !important;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 9999999;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    
        > .popup-ctr {
            animation: BodyFadeIn 150ms ease-in;
            animation-fill-mode: forwards;

            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;

            border-radius: 7px;
            padding: .25em;

            max-height: 90%;

            background-color: whitesmoke;

            > * {
                width: 100% !important;
                padding: .5em;
            }

            > header {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                border-bottom: thin solid lightgray;

                > .title {
                    font-size: 1.3em;
                    color: rgb(37, 37, 37);
                }

                > .close-button {
                    padding: .25em;
                    background: 0;
                    border: 0;
                    outline: 0;

                    &:focus {
                        outline: thin solid lightgray !important;
                    }

                    transition: background-color 150ms;
                    &:hover {
                        background-color: lightgray;
                    }
                }
            }

            > footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
                border-top: thin solid lightgray;

                > button {
                    margin-left: .5em;
                    min-width: 75px;
                    padding: .3em .7em;
                    border-radius: .25em;

                    border: 0;
                    outline: 0;

                    color: whitesmoke;

                    &:focus {
                        outline: thin solid black !important;
                    }

                    &:active {
                        outline: .125em solid black !important;
                    }

                    transition: background-color 150ms;
                }

                > .cancel {
                    background-color: var(--invalid-input);
                    &:hover {
                        background-color: var(--invalid-input--hover) !important;
                    }
                }

                > .ok {
                    background-color: var(--sustainfinity-green);
                    &:hover {
                        background-color: var(--sustainfinity-green--hover) !important;
                    }
                }
            }

            > .body {
                height: 100%;
                overflow-y: auto;
            }
        }
    }

    @keyframes BackdropFadeIn {
        0% {
            background-color: none;
        } 100% {
            background-color: rgba(28, 28, 28, 0.8);
        }
    }

    @keyframes BodyFadeIn {
        0% {
            opacity: 0;
            transform: translateY(-50%);
        } 100% {
            opacity: 1;
            transform: translateY(0);
        }
    }

    // hiidng the modal //@at-root

    .sust-popup.hide {
        animation: BackdropFadeOut 150ms ease-in !important;
        animation-fill-mode: forwards !important;
    }

    .popup-ctr.hide {
        animation: BodyFadeOut 150ms ease-in !important;
        animation-fill-mode: forwards !important;
    }

    @keyframes BackdropFadeOut {
        0% {
            background-color: rgba(28, 28, 28, 0.8);
        } 100% {
            background-color: none;
        }
    }

    @keyframes BodyFadeOut {
        0% {
            opacity: 1;
            transform: translateY(0);
        } 100% {
            opacity: 0;
            transform: translateY(-50%);
        }
    }

</style>